import React, { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import {
  socialApi,
  socialPostApi,
  socialPublicationApi,
  SocialPlattform,
  SocialPost,
  SocialVorschlag,
  SocialConnectionInfo,
} from '../services/api';
import { useToast } from '../sectepe-ui/index';
import { useConfirm } from '../components/ui';
import {
  PLATTFORM_NAME,
  X_MAX_ZEICHEN,
  X_ZIEL_ZEICHEN,
  IG_MAX_ZEICHEN,
  IG_ZIEL,
  FB_ZIEL,
  xZeichen,
  zeichen,
  tweetsVon,
  enthaeltUrl,
} from './plattformen';

export interface SocialPostStudioProps {
  platform: SocialPlattform;
  postIdeaId: string;
  contentBriefId?: string;
  provider: string;
  variants: number;
}

interface Entwurf {
  text: string;
  hashtags: string;
  tweets: string[];
}

/** Zaehler mit Farbe: rot ueber der Grenze, gelb ausserhalb des Ziels. */
const Zaehler: React.FC<{ n: number; max?: number; ziel?: { min?: number; max: number } }> = ({ n, max, ziel }) => {
  const zuViel = max !== undefined && n > max;
  const ausserhalb = !zuViel && ziel && (n > ziel.max || (ziel.min !== undefined && n < ziel.min));
  const farbe = zuViel ? 'var(--color-danger)' : ausserhalb ? 'var(--color-warning, #b45309)' : 'var(--color-text-secondary)';
  return (
    <span className="text-xs" style={{ color: farbe }} aria-live="polite">
      {n}{max !== undefined ? ` / ${max}` : ''} Zeichen{ziel ? ` (Ziel ${ziel.min !== undefined ? `${ziel.min}–` : 'bis '}${ziel.max})` : ''}
    </span>
  );
};

const leerEntwurf = (post?: SocialPost | SocialVorschlag): Entwurf => ({
  text: post?.text ?? '',
  hashtags: (post && 'hashtags' in post ? post.hashtags : '') || '',
  tweets: post ? ('tweets' in post && post.tweets ? post.tweets : tweetsVon(post as SocialPost)) : [''],
});

/**
 * Texte fuer Instagram, X oder Facebook zu einer Beitragsidee: generieren,
 * speichern, bearbeiten (Live-Zaehler, bei X je Tweet), Version waehlen und
 * mit Konto (Firma/persoenlich) zum Terminieren einplanen.
 */
export const SocialPostStudio: React.FC<SocialPostStudioProps> = ({ platform, postIdeaId, contentBriefId, provider, variants }) => {
  const name = PLATTFORM_NAME[platform];
  const [vorschlaege, setVorschlaege] = useState<SocialVorschlag[]>([]);
  const [posts, setPosts] = useState<SocialPost[]>([]);
  const [generiere, setGeneriere] = useState(false);
  const [fehler, setFehler] = useState('');
  const [bearbeiten, setBearbeiten] = useState<{ post: SocialPost; entwurf: Entwurf } | null>(null);
  const [planen, setPlanen] = useState<{ post: SocialPost; datum: string; zeit: string; konto: string } | null>(null);
  const [konten, setKonten] = useState<SocialConnectionInfo[]>([]);
  const { push } = useToast();
  const { confirm } = useConfirm();

  const laden = useCallback(async () => {
    try {
      setPosts((await socialPostApi.getByIdea(postIdeaId, platform)) ?? []);
    } catch {
      setPosts([]);
    }
  }, [postIdeaId, platform]);

  useEffect(() => {
    setVorschlaege([]);
    laden();
  }, [laden]);

  const generieren = async () => {
    setFehler('');
    setGeneriere(true);
    setVorschlaege([]);
    try {
      const r = await socialPostApi.generate({ postIdeaId, contentBriefId, platform, variants, provider });
      setVorschlaege(r?.suggestions ?? []);
    } catch (err: any) {
      setFehler(err?.response?.data?.error || 'Fehler bei der KI-Generierung. Bitte erneut versuchen.');
    } finally {
      setGeneriere(false);
    }
  };

  const speichern = async () => {
    try {
      await socialPostApi.save({
        postIdeaId,
        contentBriefId,
        platform,
        provider,
        posts: vorschlaege.map((v) => ({ text: v.text, hashtags: v.hashtags, tweets: v.tweets, versionLabel: v.versionLabel })),
      });
      setVorschlaege([]);
      await laden();
      push({ message: `${name}-Varianten gespeichert. Wähle jetzt die beste Version.`, severity: 'success' });
    } catch (err: any) {
      setFehler(err?.response?.data?.error || 'Fehler beim Speichern');
    }
  };

  const auswaehlen = async (post: SocialPost) => {
    try {
      await socialPostApi.select(post.id);
      await laden();
    } catch (err: any) {
      setFehler(err?.response?.data?.error || 'Fehler beim Auswählen');
    }
  };

  const loeschen = async (post: SocialPost) => {
    if (!(await confirm({ title: 'Version löschen?', confirmLabel: 'Löschen', tone: 'danger' }))) return;
    try {
      await socialPostApi.delete(post.id);
      await laden();
    } catch (err: any) {
      setFehler(err?.response?.data?.error || 'Fehler beim Löschen');
    }
  };

  const bearbeitungSpeichern = async () => {
    if (!bearbeiten) return;
    const { post, entwurf } = bearbeiten;
    try {
      const r = await socialPostApi.update(post.id, platform === 'x'
        ? { tweets: entwurf.tweets.filter((t) => t.trim()) }
        : platform === 'instagram' ? { text: entwurf.text, hashtags: entwurf.hashtags } : { text: entwurf.text });
      setBearbeiten(null);
      await laden();
      const fehlerTexte = r?.pruefung?.fehler ?? [];
      if (fehlerTexte.length > 0) push({ message: `Gespeichert, aber so nicht veröffentlichbar: ${fehlerTexte.join(' ')}`, severity: 'warning', duration: 0 });
    } catch (err: any) {
      setFehler(err?.response?.data?.error || 'Fehler beim Speichern');
    }
  };

  const planenOeffnen = async (post: SocialPost) => {
    try {
      const status = await socialApi.getStatus();
      const liste = (status?.connections ?? []).filter((c) => c.platform === platform && c.isActive);
      setKonten(liste);
      setPlanen({ post, datum: '', zeit: '10:00', konto: liste[0]?.id ?? '' });
    } catch {
      setKonten([]);
      setPlanen({ post, datum: '', zeit: '10:00', konto: '' });
    }
  };

  const einplanen = async () => {
    if (!planen?.datum) return;
    try {
      await socialPublicationApi.create({
        socialPostId: planen.post.id,
        scheduledDate: `${planen.datum}T${planen.zeit}:00`,
        scheduledTime: planen.zeit,
        connectionId: planen.konto || null,
      });
      setPlanen(null);
      await laden();
      push({ message: `${name}-Beitrag im Kalender eingeplant.`, severity: 'success' });
    } catch (err: any) {
      setFehler(err?.response?.data?.error || 'Fehler beim Planen');
    }
  };

  const vorschau = (v: { text: string; hashtags?: string | null; tweets?: string[] }) => {
    if (platform === 'x') {
      const tweets = v.tweets && v.tweets.length > 0 ? v.tweets : [v.text];
      return tweets.map((t, i) => (
        <div key={i} className="post-section">
          <span className="post-section-label">{tweets.length > 1 ? `Tweet ${i + 1}` : 'Tweet'} · <Zaehler n={xZeichen(t)} max={X_MAX_ZEICHEN} ziel={{ max: X_ZIEL_ZEICHEN }} /></span>
          <div className="post-section-content" style={{ whiteSpace: 'pre-wrap' }}>{t}</div>
        </div>
      ));
    }
    const gesamt = platform === 'instagram' && v.hashtags ? `${v.text}\n\n${v.hashtags}` : v.text;
    return (
      <div className="post-section">
        <span className="post-section-label">
          {platform === 'instagram' ? 'Caption' : 'Beitrag'} · <Zaehler n={zeichen(gesamt)} max={platform === 'instagram' ? IG_MAX_ZEICHEN : undefined} ziel={platform === 'instagram' ? IG_ZIEL : FB_ZIEL} />
        </span>
        <div className="post-section-content" style={{ whiteSpace: 'pre-wrap' }}>{v.text}</div>
        {platform === 'instagram' && v.hashtags && <div className="post-section-content post-footer-text">{v.hashtags}</div>}
      </div>
    );
  };

  const ueberGrenze = (p: SocialPost) => platform === 'x' && tweetsVon(p).some((t) => xZeichen(t) > X_MAX_ZEICHEN);

  return (
    <div data-testid={`social-studio-${platform}`}>
      {fehler && (
        <div className="alert alert-error">
          {fehler}
          <button onClick={() => setFehler('')} aria-label="Meldung schließen" style={{ marginLeft: '12px', background: 'none', border: 'none', cursor: 'pointer', fontWeight: 'bold' }}>x</button>
        </div>
      )}
      <div className="form-section">
        <h2 className="form-section-title">2. {name}-Texte generieren</h2>
        <p className="text-sm text-secondary">
          {platform === 'instagram' && 'Caption mit Hook in der ersten Zeile, 600–1500 Zeichen, 8–15 Hashtags, „Link in Bio“ statt URL.'}
          {platform === 'x' && 'Einzeltweet oder Thread (2–5 Tweets), je höchstens 270 Zeichen, 1–2 Hashtags, keine Links (Link-Posts kosten bei X mehr).'}
          {platform === 'facebook' && 'Beitrag für die Facebook-Seite, 300–1200 Zeichen, 0–3 Hashtags, Link erlaubt, Frage zum Schluss.'}
        </p>
        <button className="btn btn-primary" onClick={generieren} disabled={generiere}>
          {generiere ? 'Generiere...' : `${name}-Texte generieren`}
        </button>
      </div>

      {vorschlaege.length > 0 && (
        <div className="form-section">
          <div className="page-header-row">
            <h2 className="form-section-title">Generierte Varianten (Vorschau)</h2>
            <button className="btn btn-success" onClick={speichern}>Varianten speichern</button>
          </div>
          <div className="post-variants-grid">
            {vorschlaege.map((v, i) => (
              <div key={i} className="card" style={{ marginBottom: '16px' }}>
                <div className="card-header"><h3 className="card-title">Version {String.fromCharCode(65 + i)}: {v.versionLabel}</h3></div>
                {vorschau(v)}
                {v.hinweise.length > 0 && (
                  <ul className="text-xs" style={{ color: 'var(--color-warning, #b45309)' }}>
                    {v.hinweise.map((h, j) => <li key={j}>{h}</li>)}
                  </ul>
                )}
              </div>
            ))}
          </div>
        </div>
      )}

      {posts.length > 0 && (
        <div className="form-section">
          <h2 className="form-section-title">3. Version auswählen &amp; planen</h2>
          <div className="post-variants-grid">
            {posts.map((post) => (
              <div key={post.id} className={`card${post.isSelected ? ' card-selected' : ''}`} style={post.isSelected ? { borderColor: 'var(--color-success)', borderWidth: '2px' } : undefined}>
                <div className="card-header">
                  <h3 className="card-title">
                    Version {post.version}{post.versionLabel ? `: ${post.versionLabel}` : ''}
                    {post.isSelected && <span className="badge badge-success" style={{ marginLeft: '8px' }}>Ausgewählt</span>}
                    {(post.publications?.length ?? 0) > 0 && <span className="badge badge-neutral" style={{ marginLeft: '8px' }}>Eingeplant</span>}
                  </h3>
                </div>
                {vorschau({ text: post.text, hashtags: post.hashtags, tweets: platform === 'x' ? tweetsVon(post) : undefined })}
                {platform === 'x' && tweetsVon(post).some(enthaeltUrl) && (
                  <p className="text-xs" style={{ color: 'var(--color-warning, #b45309)' }}>Enthält einen Link: kostet bei X den Link-Preis. Besser in eine Antwort setzen.</p>
                )}
                <div className="card-footer" style={{ flexWrap: 'wrap', gap: '8px' }}>
                  {!post.isSelected && <button className="btn btn-success btn-sm" onClick={() => auswaehlen(post)}>Auswählen</button>}
                  <button className="btn btn-secondary btn-sm" onClick={() => setBearbeiten({ post, entwurf: leerEntwurf(post) })}>Bearbeiten</button>
                  {post.isSelected && (
                    <button className="btn btn-primary btn-sm" onClick={() => planenOeffnen(post)} disabled={ueberGrenze(post)} title={ueberGrenze(post) ? 'Ein Tweet ist länger als 280 Zeichen' : undefined}>
                      Zum Terminieren
                    </button>
                  )}
                  <button className="btn btn-danger btn-sm" onClick={() => loeschen(post)}>Löschen</button>
                </div>
              </div>
            ))}
          </div>
          <p className="text-sm text-secondary">Geplante Einträge stehen unter <Link to="/publication-calendar">Terminieren</Link>.</p>
        </div>
      )}

      {bearbeiten && (
        <div className="modal-overlay">
          <div className="modal" style={{ maxWidth: '700px' }}>
            <div className="modal-header">
              <h2 className="modal-title">{name} – Version {bearbeiten.post.version} bearbeiten</h2>
              <button onClick={() => setBearbeiten(null)} className="modal-close" aria-label="Schließen">x</button>
            </div>
            {platform === 'x' ? (
              <>
                {bearbeiten.entwurf.tweets.map((t, i) => (
                  <div className="form-group" key={i}>
                    <label className="form-label" htmlFor={`tweet-${i}`}>
                      Tweet {i + 1} · <Zaehler n={xZeichen(t)} max={X_MAX_ZEICHEN} ziel={{ max: X_ZIEL_ZEICHEN }} />
                    </label>
                    <textarea
                      id={`tweet-${i}`}
                      className="form-textarea"
                      rows={4}
                      value={t}
                      onChange={(e) => {
                        const tweets = [...bearbeiten.entwurf.tweets];
                        tweets[i] = e.target.value;
                        setBearbeiten({ ...bearbeiten, entwurf: { ...bearbeiten.entwurf, tweets } });
                      }}
                    />
                  </div>
                ))}
                <div className="btn-group">
                  {bearbeiten.entwurf.tweets.length < 5 && (
                    <button className="btn btn-secondary btn-sm" onClick={() => setBearbeiten({ ...bearbeiten, entwurf: { ...bearbeiten.entwurf, tweets: [...bearbeiten.entwurf.tweets, ''] } })}>
                      Tweet anhängen
                    </button>
                  )}
                  {bearbeiten.entwurf.tweets.length > 1 && (
                    <button className="btn btn-secondary btn-sm" onClick={() => setBearbeiten({ ...bearbeiten, entwurf: { ...bearbeiten.entwurf, tweets: bearbeiten.entwurf.tweets.slice(0, -1) } })}>
                      Letzten Tweet entfernen
                    </button>
                  )}
                </div>
              </>
            ) : (
              <>
                <div className="form-group">
                  <label className="form-label" htmlFor="social-text">
                    {platform === 'instagram' ? 'Caption' : 'Text'} ·{' '}
                    <Zaehler
                      n={zeichen(platform === 'instagram' && bearbeiten.entwurf.hashtags ? `${bearbeiten.entwurf.text}\n\n${bearbeiten.entwurf.hashtags}` : bearbeiten.entwurf.text)}
                      max={platform === 'instagram' ? IG_MAX_ZEICHEN : undefined}
                      ziel={platform === 'instagram' ? IG_ZIEL : FB_ZIEL}
                    />
                  </label>
                  <textarea id="social-text" className="form-textarea" rows={12} value={bearbeiten.entwurf.text}
                    onChange={(e) => setBearbeiten({ ...bearbeiten, entwurf: { ...bearbeiten.entwurf, text: e.target.value } })} />
                </div>
                {platform === 'instagram' && (
                  <div className="form-group">
                    <label className="form-label" htmlFor="social-hashtags">Hashtags (8–15)</label>
                    <input id="social-hashtags" className="form-input" value={bearbeiten.entwurf.hashtags}
                      onChange={(e) => setBearbeiten({ ...bearbeiten, entwurf: { ...bearbeiten.entwurf, hashtags: e.target.value } })} />
                  </div>
                )}
              </>
            )}
            <div className="btn-group mt-6">
              <button className="btn btn-success" onClick={bearbeitungSpeichern}>Speichern</button>
              <button className="btn btn-secondary" onClick={() => setBearbeiten(null)}>Abbrechen</button>
            </div>
          </div>
        </div>
      )}

      {planen && (
        <div className="modal-overlay">
          <div className="modal" style={{ maxWidth: '500px' }}>
            <div className="modal-header">
              <h2 className="modal-title">{name}-Beitrag einplanen</h2>
              <button onClick={() => setPlanen(null)} className="modal-close" aria-label="Schließen">x</button>
            </div>
            <div className="form-group">
              <label className="form-label" htmlFor="social-datum">Datum</label>
              <input id="social-datum" type="date" className="form-input" value={planen.datum} min={new Date().toISOString().split('T')[0]}
                onChange={(e) => setPlanen({ ...planen, datum: e.target.value })} />
            </div>
            <div className="form-group">
              <label className="form-label" htmlFor="social-zeit">Uhrzeit</label>
              <input id="social-zeit" type="time" className="form-input" value={planen.zeit} onChange={(e) => setPlanen({ ...planen, zeit: e.target.value })} />
            </div>
            <div className="form-group">
              <label className="form-label" htmlFor="social-konto">{platform === 'facebook' ? 'Seite' : 'Konto'}</label>
              <select id="social-konto" className="form-select" value={planen.konto} onChange={(e) => setPlanen({ ...planen, konto: e.target.value })}>
                <option value="">Später wählen</option>
                {konten.map((k) => (
                  <option key={k.id} value={k.id}>
                    {k.accountName || k.accountId} ({k.art === 'mandant' ? (platform === 'facebook' ? 'Seite' : 'Firma') : 'persönlich'})
                  </option>
                ))}
              </select>
              {konten.length === 0 && (
                <small className="form-helper">Noch kein {name}-Konto verbunden (Statistiken &amp; Konto → Konten &amp; Kanäle).</small>
              )}
            </div>
            <div className="btn-group mt-6">
              <button className="btn btn-success" onClick={einplanen} disabled={!planen.datum}>Einplanen</button>
              <button className="btn btn-secondary" onClick={() => setPlanen(null)}>Abbrechen</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default SocialPostStudio;
